import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useKeyboardOpen } from '../hooks/useViewport';

export type ViewKey = 'overview' | 'kanban' | 'list' | 'reports' | 'audit' | 'settings';

const TABS: { key: ViewKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'kanban', label: 'Pipeline' },
  { key: 'list', label: 'List' },
  { key: 'reports', label: 'Reports' },
  { key: 'audit', label: 'Audit' },
  { key: 'settings', label: 'Settings' },
];

export function TopBar({
  view,
  setView,
  onNewLead,
  onImport,
  onOpenPalette,
  onCreateOrg,
  overdueCount,
}: {
  view: ViewKey;
  setView: (v: ViewKey) => void;
  onNewLead: () => void;
  onImport: () => void;
  onOpenPalette: () => void;
  onCreateOrg: () => void;
  overdueCount: number;
}) {
  const { me, currentOrgId, switchOrg, logout } = useAuth();

  const currentOrg = me?.organizations.find((o) => o.id === currentOrgId) ?? me?.organizations[0];

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 md:px-6 py-2 md:py-3 pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-3 bg-surface border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <h1 className="text-lg font-bold whitespace-nowrap">
            <span className="text-accent">Figureit</span>
            <span className="hidden sm:inline"> CRM</span>
          </h1>
          {currentOrg && (
            <OrgSwitcher
              orgs={me?.organizations ?? []}
              currentId={currentOrg.id}
              onSwitch={switchOrg}
              onCreate={onCreateOrg}
            />
          )}
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          {/* Desktop tabs. On phones navigation lives in the bottom bar. */}
          <div className="hidden md:flex gap-1 bg-surface2 rounded-md p-0.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={clsx('nav-tab', view === t.key && 'nav-tab-active')}
                onClick={() => setView(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            className="btn btn-secondary hidden md:flex items-center gap-2"
            onClick={onOpenPalette}
            title="Quick search (⌘K)"
          >
            <span>Search</span>
            <kbd className="text-[10px] bg-surface3 text-text3 px-1 py-0.5 rounded border border-border">
              ⌘K
            </kbd>
          </button>
          <button
            className="btn btn-ghost md:hidden px-2.5"
            onClick={onOpenPalette}
            aria-label="Search leads"
            title="Search"
          >
            <SearchIcon />
          </button>
          <button className="btn btn-secondary hidden md:inline-flex" onClick={onImport}>
            Import
          </button>
          <button className="btn btn-primary hidden md:inline-flex" onClick={onNewLead}>
            + New Lead
          </button>
          <UserMenu setView={setView} onLogout={logout} onImport={onImport} />
        </div>
      </div>
      {overdueCount > 0 && (
        <div className="bg-red/10 border-b border-red/20 px-3 md:px-6 py-1.5 md:py-2 text-sm text-red flex items-center gap-2 flex-shrink-0">
          <span className="truncate">
            ⚠ {overdueCount} overdue action{overdueCount > 1 ? 's' : ''}
          </span>
          <button
            className="ml-auto btn btn-sm btn-ghost text-red hover:text-red !min-h-0 py-1"
            onClick={() => setView('list')}
          >
            View all
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Phone-only bottom tab bar (thumb reach) + raised "New lead" action. Hidden
 * while the on-screen keyboard is up so it never sits on top of the field
 * being edited.
 */
export function MobileNav({
  view,
  setView,
  onNewLead,
  onOpenPalette,
  onImport,
}: {
  view: ViewKey;
  setView: (v: ViewKey) => void;
  onNewLead: () => void;
  onOpenPalette: () => void;
  onImport: () => void;
}) {
  const keyboardOpen = useKeyboardOpen();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreViews: ViewKey[] = ['reports', 'audit', 'settings'];
  const moreActive = moreViews.includes(view);
  if (keyboardOpen) return null;

  const Tab = ({ k, label, icon }: { k: ViewKey; label: string; icon: React.ReactNode }) => {
    const active = view === k;
    return (
      <button
        className={clsx(
          'flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium min-h-[52px]',
          active ? 'text-accent2' : 'text-text3',
        )}
        onClick={() => {
          setMoreOpen(false);
          setView(k);
        }}
        aria-current={active ? 'page' : undefined}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <>
      {moreOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute left-0 right-0 bottom-[calc(60px+env(safe-area-inset-bottom,0px))] mx-3 bg-surface border border-border rounded-xl shadow-2xl overflow-hidden animate-[slideUp_0.15s_ease]"
            onClick={(e) => e.stopPropagation()}
          >
            {(
              [
                ['reports', 'Reports', '📈'],
                ['audit', 'Audit log', '🗒'],
                ['settings', 'Settings', '⚙️'],
              ] as [ViewKey, string, string][]
            ).map(([k, label, emoji]) => (
              <button
                key={k}
                className={clsx(
                  'w-full text-left px-4 py-3.5 text-[15px] flex items-center gap-3 border-b border-border',
                  view === k && 'text-accent2',
                )}
                onClick={() => {
                  setMoreOpen(false);
                  setView(k);
                }}
              >
                <span className="w-6 text-center">{emoji}</span>
                {label}
              </button>
            ))}
            <button
              className="w-full text-left px-4 py-3.5 text-[15px] flex items-center gap-3 border-b border-border"
              onClick={() => {
                setMoreOpen(false);
                onOpenPalette();
              }}
            >
              <span className="w-6 text-center">🔍</span>
              Search / jump to…
            </button>
            <button
              className="w-full text-left px-4 py-3.5 text-[15px] flex items-center gap-3"
              onClick={() => {
                setMoreOpen(false);
                onImport();
              }}
            >
              <span className="w-6 text-center">📥</span>
              Import data
            </button>
          </div>
        </div>
      )}
      <nav
        className="md:hidden flex-shrink-0 relative z-30 bg-surface border-t border-border flex items-stretch pb-[env(safe-area-inset-bottom,0px)]"
        aria-label="Primary"
      >
        <Tab k="overview" label="Overview" icon={<HomeIcon />} />
        <Tab k="kanban" label="Pipeline" icon={<ColumnsIcon />} />
        <button
          className="flex-1 flex flex-col items-center justify-center -mt-4"
          onClick={onNewLead}
          aria-label="New lead"
        >
          <span className="w-12 h-12 rounded-full bg-accent text-white flex items-center justify-center text-2xl font-bold shadow-lg shadow-accent/30 leading-none">
            +
          </span>
          <span className="text-[11px] font-medium text-text3 mt-0.5">New</span>
        </button>
        <Tab k="list" label="List" icon={<ListIcon />} />
        <button
          className={clsx(
            'flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium min-h-[52px]',
            moreActive || moreOpen ? 'text-accent2' : 'text-text3',
          )}
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
        >
          <DotsIcon />
          More
        </button>
      </nav>
    </>
  );
}

function OrgSwitcher({
  orgs,
  currentId,
  onSwitch,
  onCreate,
}: {
  orgs: { id: string; name: string; role: string }[];
  currentId: string;
  onSwitch: (id: string) => void | Promise<void>;
  onCreate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // Click/tap-outside to close.
  useEffect(() => {
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, []);
  const current = orgs.find((o) => o.id === currentId) ?? orgs[0];
  if (!current) return null;
  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="bg-surface2 border border-border rounded-md px-2.5 py-1.5 md:py-1 text-sm flex items-center gap-2 hover:bg-surface3 max-w-full min-h-[36px]"
      >
        <span className="truncate max-w-[110px] sm:max-w-[160px]">{current.name}</span>
        <span className="hidden sm:inline text-[10px] text-text3 uppercase">{current.role}</span>
        <span className="text-text3">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-[min(16rem,calc(100vw-1.5rem))] bg-surface border border-border rounded-md shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-1.5 text-[11px] text-text3 uppercase tracking-wide border-b border-border">
            Switch organization
          </div>
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => {
                if (o.id !== currentId) onSwitch(o.id);
                setOpen(false);
              }}
              className={clsx(
                'w-full text-left px-3 py-2.5 md:py-2 text-sm flex items-center justify-between hover:bg-surface2',
                o.id === currentId && 'bg-surface2',
              )}
            >
              <span className="truncate">{o.name}</span>
              <span className="text-[10px] text-text3 uppercase ml-2">{o.role}</span>
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              onCreate();
            }}
            className="w-full text-left px-3 py-2.5 md:py-2 text-sm text-accent2 hover:bg-surface2 border-t border-border"
          >
            + New organization…
          </button>
        </div>
      )}
    </div>
  );
}

function UserMenu({
  setView,
  onLogout,
  onImport,
}: {
  setView: (v: ViewKey) => void;
  onLogout: () => void | Promise<void>;
  onImport: () => void;
}) {
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn btn-ghost flex items-center gap-2 px-1.5 md:px-3.5"
        aria-label="Account menu"
      >
        {me?.avatarUrl ? (
          <img
            src={me.avatarUrl}
            alt=""
            className="w-8 h-8 md:w-7 md:h-7 rounded-full"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 md:w-7 md:h-7 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold">
            {me?.email[0]?.toUpperCase() ?? '?'}
          </div>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-surface border border-border rounded-md shadow-lg z-50">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-sm font-medium truncate">{me?.name ?? me?.email}</div>
            <div className="text-xs text-text3 truncate">{me?.email}</div>
          </div>
          <button
            onClick={() => {
              onImport();
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2.5 md:py-2 hover:bg-surface2 text-sm md:hidden"
          >
            Import data
          </button>
          <button
            onClick={() => {
              setView('settings');
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2.5 md:py-2 hover:bg-surface2 text-sm"
          >
            Settings
          </button>
          <button
            onClick={onLogout}
            className="w-full text-left px-3 py-2.5 md:py-2 hover:bg-surface2 text-sm text-red"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- Tiny inline icons (no icon lib dependency) ---- */
const iconProps = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
function HomeIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h5v-6h4v6h5V10" />
    </svg>
  );
}
function ColumnsIcon() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="9.5" y="4" width="5" height="11" rx="1" />
      <rect x="16" y="4" width="5" height="7" rx="1" />
    </svg>
  );
}
function ListIcon() {
  return (
    <svg {...iconProps}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx="4" cy="6" r="1" fill="currentColor" />
      <circle cx="4" cy="12" r="1" fill="currentColor" />
      <circle cx="4" cy="18" r="1" fill="currentColor" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
